import type { FormEvent } from "react";
import { useState } from "react";
import type { AxiosError } from "axios";
import { useResetPassword } from "../hooks";

const MIN_PASSWORD_LENGTH = 8;

type ErrorPayload = {
  detail?: string;
  message?: string;
};

type ResetPasswordFormProps = {
  onSuccess?: () => void;
  /** When true, omit outer card chrome (parent provides panel). */
  embedded?: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<ErrorPayload>;
    return (
      axiosError.response?.data?.detail ??
      axiosError.response?.data?.message ??
      axiosError.message ??
      "Unable to reset password right now."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unable to reset password right now.";
};

export const ResetPasswordForm = ({ onSuccess, embedded }: ResetPasswordFormProps) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const resetPasswordMutation = useResetPassword();
  const canSubmit =
    currentPassword.trim().length > 0 &&
    newPassword.trim().length >= MIN_PASSWORD_LENGTH &&
    confirmPassword.trim().length >= MIN_PASSWORD_LENGTH &&
    !resetPasswordMutation.isPending;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (newPassword !== confirmPassword) {
      setFormError("New passwords do not match.");
      return;
    }

    if (newPassword.trim().length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        currentPassword,
        newPassword
      });
      setFormSuccess("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onSuccess?.();
    } catch (error) {
      setFormError(getErrorMessage(error));
    }
  };

  const inner = (
    <>
      <h2>Change password</h2>
      <p>Update your password regularly to keep your account secure.</p>
      <form onSubmit={handleSubmit}>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">Current password</span>
          <div className="settings-password-row">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
            <button type="button" onClick={() => setShowCurrent((v) => !v)}>
              {showCurrent ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">New password</span>
          <div className="settings-password-row">
            <input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <button type="button" onClick={() => setShowNew((v) => !v)}>
              {showNew ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <label style={{ display: "grid", gap: "0.35rem" }}>
          <span className="settings-label">Confirm new password</span>
          <div className="settings-password-row">
            <input
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <button type="button" onClick={() => setShowConfirm((v) => !v)}>
              {showConfirm ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        {formError ? <div style={{ color: "#b91c1c", fontSize: "0.875rem" }}>{formError}</div> : null}
        {formSuccess ? <div style={{ color: "#166534", fontSize: "0.875rem" }}>{formSuccess}</div> : null}
        <div className="settings-form-actions">
          <button type="submit" disabled={!canSubmit} className="settings-btn-primary">
            {resetPasswordMutation.isPending ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return <div className="settings-form-embedded">{inner}</div>;
  }

  return <div className="settings-form-card">{inner}</div>;
};

export default ResetPasswordForm;
